import * as THREE from 'three';
import {
    CabinetMesh,
    RoomPlannerProduct,
} from 'components/customer/RoomPlanner/types.d';
import {checkCabinetOverlap} from 'components/customer/RoomPlanner/helpers/overlap';
import {legFrontDepth} from 'components/customer/RoomPlanner/helpers/calculations';
import {
    DEFAULT_SNAPPING_DISTANCE,
    KICK_PANEL_SETBACK_MM,
    SCENE_UNITS_PER_MM,
} from 'components/customer/RoomPlanner/helpers/snapping/constants';
import {
    PairFacts,
    ProductFacts,
    SnapContext,
    SnapOutcome,
    SnapStrategy,
} from 'components/customer/RoomPlanner/helpers/snapping/types';
import {
    bboxAt,
    extentAlong,
    frontOf,
    landingForBBoxMin,
    landingMeetsFloorProduct,
    planDistance,
} from 'components/customer/RoomPlanner/helpers/snapping/strategies/kickPanels';

/**
 * Kicker frames and kick-length panels resting under a cabinet (R-12,
 * CO-5716).
 * Both sit a toe kick below the cabinet, so they snap when dragged near, in
 * plan, where they will rest rather than near the cabinet's faces. Rewritten
 * in CO-5847 from getSnappingData's under-cabinet branch, which took the
 * landing along the run from the legacy face positioning.
 */

// The kicker's exterior thickness in mm, 0 when it has none.
const exteriorThicknessOf = ({group}: ProductFacts) =>
    parseInt(
        String(
            (group.userData.values as RoomPlannerProduct | undefined)
                ?.cabinet_ext_thickness ?? ''
        )
    ) || 0;

/**
 * Where a kicker frame rests under a cabinet that faces the same way (R-12):
 * across, on the cabinet's origin, moved in by a fifth of the exterior
 * thickness when the kicker has a finished left edge; front to back, by the
 * difference in depth between the two, recessed by a fifth of the exterior
 * thickness for a finished front edge and again under a panel product. Worked
 * in the cabinet's own frame, so it holds whichever way the run faces.
 * @param {ProductFacts} kicker The kicker frame, turned the same way as the
 * cabinet.
 * @param {ProductFacts} cabinet The cabinet above it.
 * @return {THREE.Vector3} The kicker's resting origin, at its own height.
 */
export const getKickerFrameLanding = (
    kicker: ProductFacts,
    cabinet: ProductFacts
) => {
    const front = frontOf(cabinet.group);
    const across = new THREE.Vector3(front.z, 0, -front.x);
    const depthOf = ({bbox}: ProductFacts) => {
        const [back, face] = extentAlong(bbox, front);

        return face - back;
    };
    const edge = kicker.group.userData.kickerFrameEdge as
        {left?: boolean; front?: boolean} | undefined;
    const recess = exteriorThicknessOf(kicker) * SCENE_UNITS_PER_MM;
    let alongFront = depthOf(kicker) - depthOf(cabinet);

    if (edge?.front) alongFront -= recess;
    if (cabinet.group.userData.isPanelProduct) alongFront -= recess;

    return cabinet.group.position
        .clone()
        .addScaledVector(across, edge?.left ? recess : 0)
        .addScaledVector(front, alongFront)
        .setY(kicker.group.position.y);
};

/**
 * A kicker frame under a cabinet facing the same way: refused while it
 * overlaps another kicker frame, since a run holds one, and where landing
 * would leave it overlapping one kicker frame among other products.
 * @param {SnapContext} ctx The calculation.
 * @param {PairFacts} pair The kicker frame and the cabinet.
 * @return {SnapOutcome} The snap, or null.
 */
const getKickerFrameSnap = (ctx: SnapContext, pair: PairFacts): SnapOutcome => {
    const {selected: kicker, other: cabinet} = pair;
    if (!pair.sameDirection) return null;
    if (ctx.overlappingCabinets?.some((mesh) => mesh.userData.isFloorProduct))
        return null;

    const landing = getKickerFrameLanding(kicker, cabinet);
    const distance = planDistance(landing, ctx.targetPos);
    if (distance > DEFAULT_SNAPPING_DISTANCE) return null;

    const overlaps = checkCabinetOverlap(
        kicker.group as CabinetMesh,
        landing,
        ctx.productsRef as CabinetMesh[],
        -1,
        true,
        false,
        {products: ctx.products}
    );
    const kickers = overlaps?.filter((mesh) => mesh.userData.isFloorProduct);
    if (kickers?.length === 1 && (overlaps?.length ?? 0) > 1) return null;

    return {position: landing, distance};
};

/**
 * Where a span starts once one of its ends is lined up with the matching end
 * of another: whichever end is nearer.
 * @param {number[]} span The span's start and end.
 * @param {number[]} to The other span's start and end.
 * @return {number} The span's new start.
 */
const nearerEndStart = (
    [start, end]: readonly [number, number],
    [min, max]: readonly [number, number]
) => (Math.abs(start - min) <= Math.abs(end - max) ? min : max - (end - start));

/**
 * Where a kick-length panel rests against a cabinet, or null. A panel running
 * along the cabinet's front rests under its front half: its back flush on the
 * front of the cabinet's legs, or, with no legs, its face
 * KICK_PANEL_SETBACK_MM behind the carcase face (the plane through the
 * cabinet's origin), and its nearer end lined up with the cabinet's end. A
 * panel running front to back is an end panel: flush against the nearer side,
 * its nearer end lined up with the cabinet's back or front.
 * @param {ProductFacts} panel The kick-length panel.
 * @param {ProductFacts} cabinet The cabinet.
 * @param {THREE.Vector3} target Where the drag puts the panel.
 * @return {THREE.Vector3 | null} The panel's resting origin, at its current
 * height.
 */
const getKickPanelLanding = (
    panel: ProductFacts,
    cabinet: ProductFacts,
    target: THREE.Vector3
) => {
    const front = frontOf(cabinet.group);
    const across = new THREE.Vector3(front.z, 0, -front.x);
    const dragged = bboxAt(panel, target);
    const panelAcross = extentAlong(dragged, across);
    const panelFront = extentAlong(dragged, front);
    const cabinetAcross = extentAlong(cabinet.bbox, across);
    const cabinetFront = extentAlong(cabinet.bbox, front);
    const thickness = panelFront[1] - panelFront[0];

    if (panelAcross[1] - panelAcross[0] <= thickness) {
        const endThickness = panelAcross[1] - panelAcross[0];
        const onRight =
            panelAcross[0] + panelAcross[1] >
            cabinetAcross[0] + cabinetAcross[1];

        return landingForBBoxMin(
            panel,
            across,
            onRight ? cabinetAcross[1] : cabinetAcross[0] - endThickness,
            front,
            nearerEndStart(panelFront, cabinetFront)
        );
    }

    const carcaseFront = cabinet.group.position.dot(front);
    const middle = (panelFront[0] + panelFront[1]) / 2;
    if (carcaseFront - middle > middle - cabinetFront[0]) return null;

    const legFront = legFrontDepth(cabinet.legBBoxes, front);
    const back =
        legFront ??
        carcaseFront - KICK_PANEL_SETBACK_MM * SCENE_UNITS_PER_MM - thickness;

    return landingForBBoxMin(
        panel,
        front,
        back,
        across,
        nearerEndStart(panelAcross, cabinetAcross)
    );
};

/**
 * A kick-length panel against a cabinet, keeping the height it was dragged
 * at; refused where a kicker frame or another kick-length panel already
 * fills the toe kick.
 * @param {SnapContext} ctx The calculation.
 * @param {PairFacts} pair The panel and the cabinet.
 * @return {SnapOutcome} The snap, or null.
 */
const getKickPanelSnap = (ctx: SnapContext, pair: PairFacts): SnapOutcome => {
    const landing = getKickPanelLanding(
        pair.selected,
        pair.other,
        ctx.targetPos
    );
    if (landing === null) return null;

    landing.y = ctx.targetPos.y;
    if (landingMeetsFloorProduct(pair.selected, landing, ctx)) return null;

    const distance = planDistance(landing, ctx.targetPos);
    if (distance > DEFAULT_SNAPPING_DISTANCE) return null;

    return {position: landing, distance};
};

// A kicker frame or a kick-length panel under a cabinet. A non-manual panel
// that is also a floor product rests as a kick-length panel.
export const getUnderCabinetSnap: SnapStrategy = (ctx, pair) =>
    pair.selected.isFloorProduct && !pair.selected.isNonManualPanel
        ? getKickerFrameSnap(ctx, pair)
        : getKickPanelSnap(ctx, pair);
